import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import styled from "@emotion/styled";
import Datetime from "react-datetime";
import moment from "moment-timezone";
import "react-datetime/css/react-datetime.css";
import { setColor } from "../../../../../assets/styles/styles";
import { default as Button } from "../../../../../components/common/button/Button";
import { uploadFile } from "../../../../../api/FilesController";
import { createRoomNew, getUserGroups, getRoomTags, getRoomPrefabs } from "../../rooms/api/roomController";
import { CACHE_QUERY_OPTIONS } from "../../../../../constants";
import Dropdown from "../../../../../components/common/inputDropdown/Dropdown";
import UserSearchDropdown from "../../../components/UserSearchDropdown";
import ToggleSwitch from "../../../../../components/common/switchToggle/ToggleSwitch";
import { createVanityLink, createVanityLinkData } from "../../../../../api/VanityLinkController";
import UploadFile from "../../../../../components/common/fileUploader/FileUploaderInput";
import Modal from "../../../components/Modal";
import AddRoomTypeModal from "../../../components/AddRoomTypeModal";

const CreateRoom = ({ setSuccessMessageBanner, setBannerType, setStepNext, setRoomId }) => {
    const token = useSelector((state) => state?.user?.token);
    const userId = useSelector((state) => state?.user?.userId);
    const navigate = useNavigate();
    const timezone = "America/Los_Angeles";
    const minDate = moment().startOf("day");
    const [buttonDisabled, setButtonDisabled] = useState(true);
    const [loading, setLoading] = useState(false);
    const [statusBanner, setStatusBanner] = useState("");
    const [statusDetail, setStatusDetail] = useState("");
    const [selectedDetailImage, setSelectedDetailImage] = useState(null);
    const [detailFileId, setDetailFileId] = useState(null);
    const [statusOwner, setStatusOwner] = useState("");
    const [ownerFileId, setOwnerFileId] = useState(null);
    const [selectedOwnerImage, setSelectedOwnerImage] = useState(null);
    const [bannerFileId, setBannerFileId] = useState(null);
    const [selectedBannerImage, setSelectedBannerImage] = useState(null);
    const [roomState, setRoomState] = useState({
        name: "",
        description: "",
        roomType: "",
        json: {},
        expiry: null,
        ownerId: "",
        tags: [],
        groupId: "",
        isVisibleInNavMenu: false,
        bannerImageKey: null,
        roomDetailsImageId: null,
        roomOwnerImageId: null,
        clientTag: "",
    });
    const [openRoomTypeModal, setOpenRoomTypeModal] = useState(false);

    const updateRoomState = (key, value) => {
        setRoomState((prevState) => ({
            ...prevState,
            [key]: value,
        }));
    };

    const { data: userGroups } = useQuery(["userGroups"], getUserGroups, {
        ...CACHE_QUERY_OPTIONS(),
    });

    const { data: roomTags } = useQuery(["roomTags"], getRoomTags, {
        ...CACHE_QUERY_OPTIONS(),
    });

    const { data: roomPrefabs } = useQuery(["roomPrefabs"], getRoomPrefabs, {
        ...CACHE_QUERY_OPTIONS(),
    });

    const setRoomType = (value) => {
        setOpenRoomTypeModal(false);
        updateRoomState("roomType", value);
    };

    const setRoomGroupId = (value) => {
        updateRoomState("groupId", value);
    };

    const handleUserClickOwner = (option) => {
        updateRoomState("ownerId", option.userId);
    };

    const handleRemoveTag = (tag) => {
        updateRoomState(
            "tags",
            roomState.tags.filter((selectedTag) => selectedTag !== tag)
        );
    };

    const handleDateChange = (date) => {
        if (moment(date).isSameOrAfter(moment(), "day")) {
            updateRoomState("expiry", date);
        }
    };

    const handleRoomTagsClick = (option) => {
        if (roomState.tags.includes(option)) {
            updateRoomState(
                "tags",
                roomState.tags.filter((tag) => tag.id !== option.id)
            );
        } else {
            updateRoomState("tags", [...roomState.tags, option]);
        }
    };

    const AddRoomSubmit = async () => {
        // setLoading(true);
        const tags = roomState.tags.map((tag) => tag.id);
        const groupId = userGroups.find((group) => group.level === roomState.groupId)?.id || null;

        try {
            const responseAddRoom = await createRoomNew({ ...roomState, tags, groupId });

            if (responseAddRoom?.status === 201) {
                const roomId = responseAddRoom?.data?.roomId;
                setRoomId(roomId);
                setSuccessMessageBanner("Room Added Successfully");
                setStepNext("step2");
            }
        } catch (err) {
            console.error("Error:", err);
            setBannerType("ERROR");
            setSuccessMessageBanner("ERROR creating new room. Try again or contact support");
        }
    };

    useEffect(() => {
        const { name, description, roomType, groupId, ownerId } = roomState;

        const hasRequiredFields = name && description && roomType && groupId && ownerId;

        const bannerReady = !selectedBannerImage || statusBanner === "completed";
        const detailReady = !selectedDetailImage || statusDetail === "completed";
        const ownerReady = !selectedOwnerImage || statusOwner === "completed";

        if (hasRequiredFields && bannerReady && detailReady && ownerReady) {
            setButtonDisabled(false);
        } else {
            setButtonDisabled(true);
        }
    }, [
        roomState,
        selectedBannerImage,
        selectedDetailImage,
        selectedOwnerImage,
        statusBanner,
        statusDetail,
        statusOwner,
    ]);

    const uploadImageApi = async (type) => {
        try {
            if (type === "bannerImg") {
                const response = await uploadFile({ file: selectedBannerImage, usageType: "roomBannerImage" }, token);
                if (response.status === 200) {
                    setBannerFileId(response?.data?.id);
                    roomState.bannerImageKey = response?.data?.id;
                    setStatusBanner("completed");
                }
                if (response.status !== 200) {
                    setStatusBanner("error");
                }
            }
            if (type === "detailImg") {
                const response = await uploadFile({ file: selectedDetailImage, usageType: "roomDetailsImage" }, token);
                if (response.status === 200) {
                    setDetailFileId(response?.data?.id);
                    roomState.roomDetailsImageId = response?.data?.id;
                    setStatusDetail("completed");
                }
                if (response.status !== 200) {
                    setStatusDetail("error");
                }
            }
            if (type === "ownerImg") {
                const response = await uploadFile({ file: selectedOwnerImage, usageType: "roomDetailsImage" }, token);
                if (response.status === 200) {
                    setOwnerFileId(response?.data?.id);
                    roomState.roomOwnerImageId = response?.data?.id;
                    setStatusOwner("completed");
                }
                if (response.status !== 200) {
                    setStatusOwner("error");
                }
            }
        } catch (error) {
            console.error("Upload failed", error);
            setStatusBanner("");
            setBannerFileId(null);
        }
    };

    const handleAddRoomTypeClick = () => {
        setOpenRoomTypeModal(true);
    };

    return (
        <>
            <Table>
                <TableRow>
                    <TableColumn>
                        <Label>Room Name*</Label>
                        <InputBox
                            type="text"
                            id="roomName"
                            placeholder="Enter Room Name"
                            value={roomState.name}
                            onChange={(e) => updateRoomState("name", e.target.value)}
                        />
                    </TableColumn>
                </TableRow>
                <TableRow>
                    <TableColumn>
                        <Label>Description*</Label>
                        <InputBox
                            type="text"
                            id="description"
                            placeholder="Enter Description"
                            value={roomState.description}
                            onChange={(e) => updateRoomState("description", e.target.value)}
                        />
                    </TableColumn>
                </TableRow>
                <TableRow style={{ padding: "5px" }}>
                    <Label style={{ marginRight: "15px" }}>Visible on Nav Menu</Label>
                    <ToggleSwitch
                        value={roomState.isVisibleInNavMenu}
                        onChange={(value) => {
                            updateRoomState("isVisibleInNavMenu", value);
                        }}
                    />
                </TableRow>
                <TableRow>
                    <TableColumn>
                        <Dropdown
                            key={"roomTypeInput"}
                            label="Room Type*"
                            items={roomPrefabs?.map((item) => ({
                                ...item,
                                value: item.roomType,
                            }))}
                            selectedValue={roomState.roomType}
                            setSelectedValue={setRoomType}
                            placeholder="Select Room Type"
                        />
                        <TextP>
                            Can't find the room type you're looking for?
                            <Button
                                variant={"NavigationButton"}
                                fontSize={"12px"}
                                margin={"5px 0 0 10px"}
                                label={"Create New Room Type"}
                                onClick={handleAddRoomTypeClick}
                            />
                        </TextP>
                    </TableColumn>
                </TableRow>

                <TableRow>
                    <TableColumn>
                        <Dropdown
                            key={"userGroupInput"}
                            label="User Group*"
                            items={userGroups?.map((item) => ({
                                ...item,
                                value: item.level,
                            }))}
                            selectedValue={roomState.groupId}
                            setSelectedValue={setRoomGroupId}
                            placeholder="Select User Group"
                        />
                    </TableColumn>
                </TableRow>

                <TableRow>
                    <TableColumn>
                        <Dropdown
                            key={"roomTagsInput"}
                            label="Room Tags"
                            items={roomTags?.map((item) => ({
                                ...item,
                                value: item.label,
                            }))}
                            selectedValue={roomState.tags}
                            setSelectedValue={handleRoomTagsClick}
                            placeholder="Select Room Tags"
                            tags
                            removeTags={handleRemoveTag}
                        />
                    </TableColumn>
                </TableRow>

                {/* <TableRow>
                    <TableColumn>
                        <Label>Room Expiry</Label>
                        <CustomDatetime
                            onChange={handleDateChange}
                            dateFormat="YYYY-MM-DD"
                            timeFormat="HH:mm:ss"
                            utc
                            inputProps={{
                                placeholder: "Choose Date",
                                value: roomState.expiry
                                    ? moment(roomState.expiry).tz(timezone).format("YYYY-MM-DD HH:mm:ss")
                                    : "",
                            }}
                            isValidDate={(current) => current.isAfter(minDate)}
                        />
                    </TableColumn>
                </TableRow> */}

                <TableRow>
                    <TableColumn>
                        <Label>Client Tag</Label>
                        <InputBox
                            type="text"
                            id="clientTag"
                            placeholder="Enter Client Tag"
                            value={roomState.clientTag}
                            onChange={(e) => updateRoomState("clientTag", e.target.value)}
                        />
                    </TableColumn>
                </TableRow>

                <TableRow>
                    <TableColumn>
                        <UserSearchDropdown
                            label="Owner Email*"
                            onSelection={handleUserClickOwner}
                            selectedUser={roomState.ownerId}
                            addOption={"true"}
                        />
                    </TableColumn>
                </TableRow>
                {roomState.ownerId && (
                    <>
                        <TableRow>
                            <TableColumn>
                                <Label>Owner Id</Label>
                                <InputBox type="text" id="ownerName" value={roomState.ownerId} disabled />
                            </TableColumn>
                        </TableRow>
                    </>
                )}

                <UploadFile
                    label="Banner Image"
                    accept={["image/png", "image/jpeg", "image/jpg"]}
                    uploadFn={() => uploadImageApi("bannerImg")}
                    selectedFile={selectedBannerImage}
                    setSelectedFile={setSelectedBannerImage}
                    status={statusBanner}
                    setStatus={setStatusBanner}
                    fileId={bannerFileId}
                />

                <UploadFile
                    label="Room Logo/Detail Image"
                    accept={["image/png", "image/jpeg", "image/jpg"]}
                    uploadFn={() => uploadImageApi("detailImg")}
                    selectedFile={selectedDetailImage}
                    setSelectedFile={setSelectedDetailImage}
                    status={statusDetail}
                    setStatus={setStatusDetail}
                    fileId={detailFileId}
                />
                <UploadFile
                    label="Room Owner Image"
                    accept={["image/png", "image/jpeg", "image/jpg"]}
                    uploadFn={() => uploadImageApi("ownerImg")}
                    selectedFile={selectedOwnerImage}
                    setSelectedFile={setSelectedOwnerImage}
                    status={statusOwner}
                    setStatus={setStatusOwner}
                    fileId={ownerFileId}
                />

                <TableRow>
                    <Button
                        variant={"PinkButton"}
                        buttonWidth={"100%"}
                        disabled={buttonDisabled}
                        label={"Create Room → Proceed to Step 2"}
                        onClick={AddRoomSubmit}
                    />
                </TableRow>
            </Table>
            {openRoomTypeModal && (
                <Modal
                    isOpen={openRoomTypeModal}
                    setIsOpen={setOpenRoomTypeModal}
                    children={<AddRoomTypeModal setRoomType={setRoomType} />}
                />
            )}
        </>
    );
};

export default CreateRoom;

const Table = styled.div`
    display: flex;
    flex-direction: column;
    padding: 1rem;
    border-radius: 5px;
`;

const TableRow = styled.div`
    display: flex;
    flex-direction: row;
    margin-bottom: 10px;
`;

const TableColumn = styled.div`
    flex: 1;
    padding: 5px;
`;

const Label = styled.div`
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 10px;
`;

const TextP = styled.div`
    font-size: 14px;
`;

const InputBox = styled.input`
    padding: 10px;
    border: ${(props) => (props.errorMessage ? `1px solid ${setColor.errorColor}` : `1px solid ${setColor.grey1}`)};
    border-radius: 5px;
    color: ${setColor.inputText};
    margin: 0rem 0rem 0rem 0rem;
    width: ${(props) => (props.width ? props.width : "100%")};
    background-color: transparent;
    margin-bottom: ${(props) => (props.errorMessage ? "0.5rem" : "0rem")};
    &:disabled {
        background-color: ${setColor.grey1};
    }
`;

const CustomDatetime = styled(Datetime)`
    .rdtDisabled {
        cursor: not-allowed !important;
        color: #ccc;
    }
    .rdt {
        background-color: #504c5c;
    }
    .rdtPicker {
        background-color: #504c5c;
        width: 100%;
        border-radius: 5px;
    }
    .rdtTimeToggle {
        background-color: #504c5c;
    }
    .rdtDays {
        background-color: #504c5c;
    }
    .rdtTime {
        background-color: #504c5c;
    }
    input {
        padding: 10px;
        border: ${(props) => (props.errorMessage ? `1px solid ${setColor.errorColor}` : `1px solid ${setColor.grey1}`)};
        border-radius: 5px;
        color: ${setColor.inputText};
        margin: 0rem 0rem 0rem 0rem;
        width: ${(props) => (props.width ? props.width : "100%")};
        background-color: transparent;
        margin-bottom: ${(props) => (props.errorMessage ? "0.5rem" : "0rem")};
        &:disabled {
            background-color: ${setColor.grey1};
        }
    }
`;
